import React, { useState } from "react";
import { KpiIcon } from "../../KpiIcons";
import AccordionPanel from "./AccordionPanel";
import AuditTrailModal from "./AuditTrailModal";
import { fmt, isOk } from "./utils";
import { DeleteButton, PrimaryButton, SecondaryButton } from "../../../../../../globalComponents/ui/Button";
import { Modal, ModalHeader } from "../../../../../../globalComponents/ui/Modal";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config.json";

/* ── tiny status pill ── */
const StatusPill = ({ status }) => {
  const s = (status || "pending").toLowerCase();
  const map = {
    approved: { bg: "var(--green2)", color: "var(--green)", label: "Accepted" },
    rejected: { bg: "var(--red2)", color: "var(--red)", label: "Rejected" },
    draft: { bg: "var(--bdr2)", color: "var(--ink3)", label: "Draft" },
    pending: { bg: "var(--amber2)", color: "var(--amber)", label: "Pending" },
  };
  const { bg, color, label } = map[s] || map.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", borderRadius: 20, fontSize: 11,
      fontWeight: 700, background: bg, color,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, display: "inline-block" }} />
      {label}
    </span>
  );
};

/* ── document icon ── */
const DocBadge = ({ doc }) => {
  if (!doc) return <span style={{ fontSize: 11, color: "var(--ink4)" }}>—</span>;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 5,
      background: "var(--blue3)", color: "var(--blue)",
      fontSize: 11, fontWeight: 600, cursor: "pointer",
    }}>
      <KpiIcon name="audit" size={10} color="var(--blue)" /> doc
    </span>
  );
};

const HistoryRow = ({
  answer,
  idx,
  locs,
  questionId,
  financialYearId,
  requiredEvidences,
  isEven,
  onSave,
  canEdit,
  onChat,
  isChatOpen,
  unitSetting,
  onDelete,
  hasPermission
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data`,
        {},
        { id: answer.id },
        "DELETE",
        false,
        false
      );
      if (isOk(res)) {
        onDelete?.();
        setShowDeleteModal(false);
      } else {
        alert(res?.message || "Failed to delete record");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("An error occurred while deleting the record");
    } finally {
      setIsDeleting(false);
    }
  };

  /* ── location name — robust string/number comparison ── */
  const locName = (() => {
    const lid = answer.location_id ?? answer.locationId;
    if (!lid) return "—";
    const found = locs.find((l) => String(l.id) === String(lid));
    return found?.name || found?.unitCode || String(lid);
  })();

  const fromDate = (answer.from_date || answer.fromDate || "")?.slice(0, 10);
  const toDate = (answer.to_date || answer.toDate || "")?.slice(0, 10);

  const answeredBy = answer.createdBy
    ? `${answer.createdBy.first_name || ""} ${answer.createdBy.last_name || ""}`.trim() || answer.createdBy.email
    : "—";

  const answeredDate = answer.updatedAt || answer.createdAt
    ? new Date(answer.updatedAt || answer.createdAt).toLocaleDateString()
    : "—";

  const displayValue = `${answer.value ?? "—"} ${answer.unit || ""}`.trim();

  return (
    <>
      {/* ── Summary row ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "13px 0",
          borderBottom: expanded ? "none" : "1px solid var(--bdr2)",
          background: isChatOpen ? "var(--blue3)" : isEven ? "var(--card)" : "#fff",
          cursor: "pointer",
          transition: "background .15s",
        }}
        onClick={() => setExpanded((p) => !p)}
      >
        {/* Expand chevron */}
        <div style={{ width: 52, display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 22, height: 22, borderRadius: 6,
            background: expanded ? "var(--blue2)" : "var(--bdr2)",
            transition: "all .15s",
          }}>
            <KpiIcon
              name="chevron"
              size={10}
              color={expanded ? "var(--blue)" : "var(--ink3)"}
              style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s" }}
            />
          </span>
        </div>

        {/* Date range */}
        <div style={{ flex: 1, padding: "0 10px", minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>
            {fromDate && toDate ? `${fmt(fromDate)} → ${fmt(toDate)}` : fromDate || "—"}
          </div>
        </div>

        {/* Location */}
        <div style={{ width: 130, flexShrink: 0, padding: "0 10px", fontSize: 12, color: "var(--ink2)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {locName}
        </div>

        {/* Value */}
        <div style={{ width: 120, flexShrink: 0, padding: "0 10px" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)" }}>{displayValue}</div>
        </div>

        {/* Document */}
        <div style={{ width: 90, flexShrink: 0, padding: "0 10px" }}>
          <DocBadge doc={answer.document} />
        </div>

        {/* Answered by */}
        <div style={{ width: 140, flexShrink: 0, padding: "0 10px", fontSize: 11.5, color: "var(--ink3)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {answeredBy}
        </div>

        {/* Answer date */}
        <div style={{ width: 110, flexShrink: 0, padding: "0 10px", fontSize: 11.5, color: "var(--ink3)" }}>
          {answeredDate}
        </div>

        {/* Status */}
        <div style={{ width: 105, flexShrink: 0, padding: "0 10px", textAlign: "center" }}>
          <StatusPill status={answer.status} />
        </div>

        {/* Action column (Chat, Audit, Delete) */}
        <div style={{ width: 120, flexShrink: 0, padding: "0 10px", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          {/* Chat */}
          <button
            className="ahp-btn-action"
            style={{
              width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              background: isChatOpen ? "var(--blue2)" : "transparent",
              color: isChatOpen ? "var(--blue)" : "var(--ink3)",
              transition: "all 0.2s ease", border: "none"
            }}
            onClick={(e) => { e.stopPropagation(); onChat?.(answer); }}
            title="Chat History"
          >
            <i className="fas fa-comment-dots" style={{ fontSize: 13 }} />
          </button>

          {/* Audit Trail */}
          <button
            className="ahp-btn-action"
            style={{
              width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              background: "transparent", color: "var(--ink3)",
              transition: "all 0.2s ease", border: "none"
            }}
            onClick={(e) => { e.stopPropagation(); setShowAudit(true); }}
            title="Audit Trail"
          >
            <i className="fas fa-history" style={{ fontSize: 13 }} />
          </button>

          {/* Delete */}
          {hasPermission("DELETE_KPI_DATA_RECORD") && (
            <button
              className="ahp-btn-action-danger"
              style={{
                width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", color: "var(--ink3)",
                transition: "all 0.2s ease", border: "none"
              }}
              onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }}
              title="Delete Record"
            >
              <i className="fas fa-trash" style={{ fontSize: 13 }} />
            </button>
          )}
        </div>

        {/* Right spacer */}
        <div style={{ width: 10, flexShrink: 0 }} />
      </div>

      {/* ── Expanded accordion panel ── */}
      {expanded && (
        <AccordionPanel
          answer={answer}
          locs={locs}
          questionId={questionId}
          financialYearId={financialYearId}
          requiredEvidences={requiredEvidences}
          onSave={() => { onSave?.(); }}
          onCancel={() => setExpanded(false)}
          canEdit={canEdit}
          onChat={onChat}
          unitSetting={unitSetting}
          onOpenAuditTrail={() => setShowAudit(true)}
          hasPermission={hasPermission}
        />
      )}

      {/* ── Audit Trail Modal ── */}
      <AuditTrailModal
        show={showAudit}
        onHide={() => setShowAudit(false)}
        answer={answer}
        questionId={questionId}
        financialYearId={financialYearId ?? answer.financial_year_id}
        locs={locs}
      />

      {/* ── Delete Confirmation Modal ── */}
      <Modal show={showDeleteModal} onClose={() => !isDeleting && setShowDeleteModal(false)} maxWidth={400}>
        <ModalHeader
          title="Delete Record"
          subtitle="Are you sure you want to delete this quantitative data record? This action cannot be undone."
          onClose={() => !isDeleting && setShowDeleteModal(false)}
        />
        <div style={{ padding: "20px 24px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <SecondaryButton onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
            Cancel
          </SecondaryButton>
          <DeleteButton onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete Record"}
          </DeleteButton>
        </div>
      </Modal>
    </>
  );
};

export default HistoryRow;